import { useState } from "react";
import { BotIcon, UserIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Message } from "../types";

type ChatMessageProps = { message: Message };

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";
  const isLong = message.content.length > 400;

  const [isExpanded, setIsExpanded] = useState(false);

  const displayContent =
    isLong && !isExpanded
      ? message.content.slice(0, 400) + "..."
      : message.content;

  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isUser ? "bg-violet-600" : "bg-muted"}`}
      >
        {isUser ? (
          <UserIcon className="w-3.5 h-3.5 text-white" />
        ) : (
          <BotIcon className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </div>

      <div
        className={`flex flex-col gap-1 max-w-[80%] ${isUser ? "items-end" : "items-start"}`}
      >
        <div
          className={`px-3 py-2 rounded-lg text-sm leading-relaxed ${isUser ? "bg-violet-600 text-white rounded-tr-sm" : "bg-muted text-foreground rounded-tl-sm"}`}
        >
          {isUser ? (
            message.content
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert text-sm leading-relaxed break-words">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <table className="border-collapse border border-border text-sm my-2">
                      {children}
                    </table>
                  ),
                  th: ({ children }) => (
                    <th className="border border-border px-3 py-1 bg-muted font-medium">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-border px-3 py-1">
                      {children}
                    </td>
                  ),
                  sub: ({ children }) => (
                    <sub className="text-xs">{children}</sub>
                  ),
                  sup: ({ children }) => (
                    <sup className="text-xs">{children}</sup>
                  ),
                }}
              >
                {displayContent}
              </ReactMarkdown>
            </div>
          )}
          {isLong && !isUser && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-xs font-medium text-violet-600 hover:text-violet-700 flex items-center gap-1 transition-colors"
            >
              {isExpanded ? "Show less" : "Read more"}
              <span className="text-[10px]">↓</span>
            </button>
          )}
        </div>
        <span className="text-xs text-muted-foreground px-1">
          {message.timestamp}
        </span>
      </div>
    </div>
  );
}
